import { createClient } from "@supabase/supabase-js";
import type {
  BootstrapData,
  OperationResult,
  PaymentInput,
  TicketPage,
} from "./types";

const url = import.meta.env.VITE_SUPABASE_URL as string;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const functionName =
  (import.meta.env.VITE_POS_API_FUNCTION as string) || "pos-api";
if (!url || !anonKey)
  console.warn("Falta configurar VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY.");
const client = createClient(
  url || "https://invalid.supabase.co",
  anonKey || "invalid",
  { auth: { persistSession: false } },
);

let sessionToken = localStorage.getItem("casaHawaPosSession") || "";
let sessionExpiresAt = localStorage.getItem("casaHawaPosSessionExpiresAt") || "";
export const setSessionToken = (token: string, expiresAt = "") => {
  sessionToken = token;
  sessionExpiresAt = token ? expiresAt || sessionExpiresAt : "";
  if (token) {
    localStorage.setItem("casaHawaPosSession", token);
    if (sessionExpiresAt)
      localStorage.setItem("casaHawaPosSessionExpiresAt", sessionExpiresAt);
  } else {
    localStorage.removeItem("casaHawaPosSession");
    localStorage.removeItem("casaHawaPosSessionExpiresAt");
  }
};
export const getSessionToken = () => {
  if (sessionExpiresAt && new Date(sessionExpiresAt).getTime() <= Date.now())
    setSessionToken("");
  return sessionToken;
};

async function invoke<T>(
  action: string,
  payload: Record<string, unknown> = {},
  timeoutMs = 15000,
): Promise<T> {
  if (!navigator.onLine && action !== "loginPOS")
    throw new Error(
      "Sin internet. El carrito está guardado; cobra cuando vuelva la conexión.",
    );
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const { data, error } = await client.functions.invoke(functionName, {
      body: { action, sessionToken, payload },
      headers: sessionToken ? { "x-pos-session": sessionToken } : undefined,
      signal: controller.signal,
    });
    if (error) {
      const context = (error as { context?: unknown }).context;
      const server =
        context && typeof (context as Response).clone === "function"
          ? ((await (context as Response)
              .clone()
              .json()
              .catch(() => null)) as { error?: string; message?: string; rejected?: boolean } | null)
          : null;
      const message =
        server?.error ||
        server?.message ||
        error.message ||
        "No se pudo contactar con Casa Hawa POS.";
      if (/sesión (?:caducada|no válida)/i.test(message)) setSessionToken("");
      throw Object.assign(new Error(message), { rejected: server?.rejected === true });
    }
    if (!data || data.ok === false) {
      const message =
        data?.error || data?.message || "La operación no fue confirmada.";
      if (/sesión (?:caducada|no válida)/i.test(message)) setSessionToken("");
      throw new Error(message);
    }
    return data as T;
  } catch (error) {
    if ((error as Error).name === "AbortError")
      throw new Error(
        "La operación tardó demasiado. Reintenta con la misma venta; no se duplicará.",
      );
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export const posApi = {
  async loginPOS(name: string, pin: string) {
    const result = await invoke<{
      ok: true;
      sessionToken: string;
      expiresAt: string;
      user: BootstrapData["user"];
    }>("loginPOS", { name, pin });
    setSessionToken(result.sessionToken, result.expiresAt);
    return result;
  },
  logoutPOS: async () => {
    try {
      await invoke("logoutPOS");
    } finally {
      setSessionToken("");
    }
  },
  bootstrapPOS: (locationId?: string) =>
    invoke<BootstrapData>("bootstrapPOS", { locationId }, 12000),
  openShift: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("openShift", payload, 12000),
  closeShift: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("closeShift", payload, 12000),
  checkoutPOS: (payload: {
    idempotencyKey: string;
    locationId: string;
    shiftId: string;
    customerId?: string;
    lines: unknown[];
    payments: PaymentInput[];
    saleDiscountType: string;
    saleDiscountValue: number;
    discountReason?: string;
    authorizationId?: string;
    authorizationContent?: unknown;
  }) => invoke<OperationResult>("checkoutPOS", payload, 15000),
  authorizePOSAction: (payload: Record<string, unknown>) =>
    invoke<{ ok: true; authorizationId: string; expiresAt: string }>(
      "authorizePOSAction",
      payload,
      10000,
    ),
  searchPOSSales: (query: string, shiftId: string, cursor?: string) =>
    invoke<TicketPage>("searchPOSSales", { query, shiftId, cursor }, 10000),
  getPOSSaleTicket: (saleId: string) =>
    invoke<Record<string, unknown>>("getPOSSaleTicket", { saleId }, 10000),
  returnPOSSale: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("returnPOSSale", payload, 15000),
  registerPOSCashMovement: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("registerPOSCashMovement", payload, 12000),
  savePOSCustomer: (payload: Record<string, unknown>) =>
    invoke<OperationResult & { customer: import("./types").Customer }>(
      "savePOSCustomer",
      payload,
      12000,
    ),
  searchPOSCustomers: (query: string) =>
    invoke<{ ok: true; items: import("./types").Customer[] }>(
      "searchPOSCustomers",
      { query },
      10000,
    ),
  getPOSPanel: (shiftId: string) =>
    invoke<Record<string, unknown>>("getPOSPanel", { shiftId }, 10000),
  createPOSTransfer: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("createPOSTransfer", payload, 15000),
  receivePOSTransfer: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("receivePOSTransfer", payload, 15000),
  resolvePOSTransferDifference: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("resolvePOSTransferDifference", payload, 15000),
  retryPOSPrint: (printJobId: string) =>
    invoke<{ ok: true; printJob: import("./types").PrintJob }>(
      "retryPOSPrint",
      { printJobId },
    ),
  completePOSPrint: (printJobId: string, success: boolean, error = "") =>
    invoke<{ ok: true }>("completePOSPrint", { printJobId, success, error }),
  openCashDrawer: (payload: Record<string, unknown>) =>
    invoke<OperationResult>("openCashDrawer", payload, 10000),
};

export function subscribePOSUpdates(onChange: () => void): () => void {
  if (
    import.meta.env.VITE_DISABLE_REALTIME === "true" ||
    !url ||
    url.includes("invalid.supabase.co")
  )
    return () => undefined;
  const channel = client
    .channel("pos-updates")
    .on("broadcast", { event: "changed" }, onChange)
    .subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}

export function operationKey(prefix: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `${prefix}_${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}
